/**
 * POINT_ROLL (craps) — shooter rolls for the established point.
 *
 * Continue-loop dice persistence: when the throw doesn't decide
 * (point not hit, no seven-out), server flow is POINT_ROLL ->
 * POINT_RESULT -> POINT_ROLL. The dice stay where they landed and
 * the shooter picks them back up. Detection:
 *   - onExit:  `update.gameState === POINT_RESULT` → continue. Skip
 *              the resolution chain entirely (no flyout, dice stay
 *              on the table, no handoff to fire).
 *              `update.gameState === SIDEBET_BETTING` → a player
 *              requested the mid-point sidebet window while the shooter
 *              was idle / dragging (immediate interrupt). No roll
 *              resolved, so skip the resolution chain too; the dice are
 *              cleared by SIDEBET_BETTING.onEnter and re-spawned when
 *              POINT_ROLL resumes.
 *   - onEnter: `update.previousState === POINT_RESULT` → continue
 *              re-entry. Skip the betting-exit / camera setup; just
 *              re-authorize the same shooter.
 *              `update.previousState === POINT_ESTABLISHED` → first
 *              point roll after the come-out. Same skip applies (no
 *              spectator re-stage, no opponent-dice rebuild).
 *
 * Terminal exits (POINT_ROLL -> SHOOTER_SET_STACK on point-hit /
 * seven-out) take the standard onExit chain. Dice cleanup happens in
 * SHOOTER_SET_STACK's enterBettingMode.
 */

import type { GameUpdate } from '@sdl/shared';
import { CrapsState } from '@sdl/shared';
import { State } from '../State.js';

export class PointRollState extends State {
  private _enterShooterId: string | null = null;

  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;
    const shooterId = update.currentPlayerId ?? update.bankerId;

    this._enterShooterId = shooterId ?? null;
    this._resetRollSetup();

    const continueFromExistingDice =
      update.previousState === CrapsState.POINT_RESULT
      || update.previousState === CrapsState.POINT_ESTABLISHED;
    if (continueFromExistingDice) {
      this._updateProfiles(update);
      await this._checkPlayers(update);
      // Initial point-roll authorization rides a follow-up update (post-broadcast
      // auto-authorize) → may land in onEnter or onUpdate; keyed-once either way.
      this._maybeSetupLocalRoll(update);
      return;
    }

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();
    game.instructionBanner?.hide();

    await game.exitBettingMode({
      animated: update.animated,
      previousState: update.previousState,
    });

    this._updateProfiles(update);
    await this._checkPlayers(update);

    this._showOpponentReadyDice(shooterId, 2);

    this._maybeSetupLocalRoll(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    const hasWins = !!(update.rollWins && update.rollWins.length > 0);

    // When a win is riding this update, DEFER the profile refresh until after
    // the flyout + balance bump. _updateProfiles → refreshFromUpdate writes the
    // new (post-win) balance straight onto the card; if it runs first, the bump
    // reads its "from" off an already-updated card, sees from == to, and no-ops
    // (the balance silently jumps instead of counting up). onExit avoids this by
    // never refreshing profiles before the flyout — mirror that here. The bump's
    // tween then guards the balance text against the deferred refresh.
    if (!hasWins) this._updateProfiles(update);
    await this._checkPlayers(update);

    // A continue roll is a same-state POINT_ROLL → POINT_ROLL transition, so it
    // lands here in onUpdate (not onExit). It can resolve a 'place' sidebet
    // (e.g. a 5/9 wins on a 5 that isn't the point) — the win rides rollWins.
    // Play it in sequence FIRST; playRollResolution skips the dice flyout since
    // a continue is non-decisive (no lastRollResult). This keeps the sidebet
    // flyout choreographed instead of racing in via an immediate event.
    if (hasWins) {
      // playRollResolution's cash flyout drives the profile balance count-up
      // itself (synced to the notes flying in), so no separate bump call here.
      await this.game.playRollResolution(update);
      // Deferred refresh — runs while the bump tween owns the balance text.
      this._updateProfiles(update);
    }

    // Re-pickup, AFTER the sidebet flyout. _maybeSetupLocalRoll keys on
    // customState.pointRolls, so a non-deciding roll (pointRolls grew) re-fires
    // the setup once the shooter is re-authorized — even though the server bumps
    // pointRolls on the transition update but re-authorizes on the follow-up.
    // On a continue the persisted dice are already in scene (count == expected),
    // so setupLocalRoll just re-enables pickup, no re-spawn (CLAUDE.md rule #5).
    this._maybeSetupLocalRoll(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // No roll resolved on this exit when we're either looping (POINT_RESULT)
    // or being interrupted by a sidebet request (SIDEBET_BETTING). Skip the
    // resolution chain entirely — no flyout, no handoff. Dice are kept on
    // the table for the loop, or cleared by SIDEBET_BETTING.onEnter's
    // enterBettingMode for the interrupt; either way the resumed POINT_ROLL
    // re-spawns / re-uses them as appropriate.
    if (update.gameState === CrapsState.POINT_RESULT
        || update.gameState === CrapsState.SIDEBET_BETTING) {
      return;
    }
    await this._checkPlayers(update);
    await this.game.playRollResolution(update);

    const next = update.currentPlayerId ?? update.bankerId ?? null;
    const prev = this._enterShooterId;
    if (prev && next && prev !== next) {
      await this.game.runShooterHandoff(update, prev, next);
    }

    // Spawn the next round's banker zone NOW so it's in 3D scene before
    // the rolling camera starts lerping toward the betting anchor. The
    // camera path passes over the zone's ground center mid-lerp; with
    // the zone already in scene the user sees it on the way in instead
    // of "popping in" at the betting view. SHOOTER_SET_STACK.onEnter
    // → enterBettingMode → primeBetZones is idempotent.
    this.game.primeBetZones();
  }
}
